import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { get }from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import Actions from "src/components/actionbar";
import { SyncScrollLayout } from "../syncScrollLayout";
import { Account } from "src/types/accounts";
import { PasswordProtectionEditor, PasswordProtectionViewer, usePasswordProtection } from "./components/passwordProtection";
import { FavIconEditor } from "./components/favIcon";
import ImageTools from "src/legacy/services/ImageTools";
import { GatingCustomizationEditor, GatingCustomizationViewer, useGatingCustomization } from "./components/gating";
import { NDACustomizationEditor, NDACustomizationViewer, useNDACustomization } from "./components/nda";
import { OtpCustomizationEditor, OtpCustomizationViewer, useOtpCustomization, } from "./components/oneTimePassword";
import { ThemeCustomizationEditor, ThemeCustomizationViewer } from "./components/theme";
import { themeAndGatingWidgetsEnum } from "src/types/settings/viewerSettings";

type ThemeAndGatingViewerProps = {
  data: Account | {};
  handleChange?: (field: string, value: any) => void;
  showEditor: boolean;
  toggleEditor: () => void;
}

const FavIcon = '/images/favicon-196x196.png'; 
const CompanyLogo = '/images/Paperflite.png';
const DefaultBanner = '/images/settings/viewerSettings/gatingBanner.png'; 


export const ThemeAndGatingViewer = forwardRef<any, ThemeAndGatingViewerProps>(({
   data,
   handleChange,
   showEditor,
   toggleEditor,
}, ref) => {
  
  const initialDataRef = useRef(get(data, "librarySettings", {})); // initial data for password protection, gating, nda, otp customizations
  
  const getTheFormData = (key: string) => ({ // based on the key, get the form data from the account data
    headerText: get(data, `librarySettings.${key}.heading`, ""),
    descriptionText: get(data, `librarySettings.${key}.descriptionText`, ""),
    buttonText: get(data, `librarySettings.${key}.buttonText`, ""),
    inputPlaceholderText: get(data, `librarySettings.${key}.placeHolder`, ""),
  });

  // Hooks for each configuration
  // Eg: passwordProtection will expose header, description, button, inputPlaceholder. Each of these will have Editor, Renderer, output, reset
  // Header, Descripton - TinyMce , Button, InputPlaceholder - useInputEditor
  const { header: PasswordHeader, description: PasswordDescription, button: PasswordButton, inputPlaceholder: PasswordInputPlaceholder } = usePasswordProtection(getTheFormData(themeAndGatingWidgetsEnum.passwordText));
  const { header: GatingHeader, description: GatingDescription, button: GatingButton, inputPlaceholder: GatingInputPlaceholder } = useGatingCustomization(getTheFormData(themeAndGatingWidgetsEnum.gatingText));
  const { header: NDAHeader, description: NDADescription, button: NDAButton, inputPlaceholder: NDAInputPlaceholder } = useNDACustomization(getTheFormData(themeAndGatingWidgetsEnum.ndaText));
  const { header: OTPHeader, description: OTPDescription, button: OTPButton } = useOtpCustomization(getTheFormData(themeAndGatingWidgetsEnum.otpText));

  // change configuration
  const changeConfigurations = { // based on the key, get the render outputs for that each form
    [themeAndGatingWidgetsEnum.passwordText] : {
      key: themeAndGatingWidgetsEnum.passwordText,
      render: {
        header: PasswordHeader.output,
        description: PasswordDescription.output,
        button: PasswordButton.output,
        inputPlaceholder: PasswordInputPlaceholder.output,
      },
    },
   [themeAndGatingWidgetsEnum.gatingText] : {
      key: themeAndGatingWidgetsEnum.gatingText,
      render: {
        header: GatingHeader.output,
        description: GatingDescription.output,
        button: GatingButton.output,
        inputPlaceholder: GatingInputPlaceholder.output,
      },
    },
    [themeAndGatingWidgetsEnum.ndaText] :{
      key: themeAndGatingWidgetsEnum.ndaText,
      render: {
        header: NDAHeader.output,
        description: NDADescription.output,
        button: NDAButton.output,
        inputPlaceholder: NDAInputPlaceholder.output,
      },
    },
    [themeAndGatingWidgetsEnum.otpText] :{
      key: themeAndGatingWidgetsEnum.otpText,
      render: {
        header: OTPHeader.output,
        description: OTPDescription.output,
        button: OTPButton.output,
      },
    },
  };

  const handleFormChange = (key: string, renders: any) => { // based on the key, trigger handleChange with updated data like ndaText, gatingText etc.,
    handleChange?.(`librarySettings.${key}`, {
      heading: renders.header,
      descriptionText: renders.description,
      buttonText: renders.button,
      ...(renders.inputPlaceholder && {
        placeHolder: renders.inputPlaceholder,
      }),
    });
  };

  const useChangeEffect = (key: string, renders: any) => { // listens to changes of data like header, description etc., and triggers handleChange
   const { header, description, button, inputPlaceholder } = renders;
   useEffect(() => {
        handleFormChange(key, renders);
    }, [  header, description, button,inputPlaceholder ]);
  };

  // change effects for each configuration
  useChangeEffect(themeAndGatingWidgetsEnum.passwordText, get(changeConfigurations, `${themeAndGatingWidgetsEnum.passwordText}.render`));
  useChangeEffect(themeAndGatingWidgetsEnum.gatingText, get(changeConfigurations, `${themeAndGatingWidgetsEnum.gatingText}.render`));
  useChangeEffect(themeAndGatingWidgetsEnum.ndaText, get(changeConfigurations, `${themeAndGatingWidgetsEnum.ndaText}.render`));
  useChangeEffect(themeAndGatingWidgetsEnum.otpText, get(changeConfigurations, `${themeAndGatingWidgetsEnum.otpText}.render`));

  // Reset the input editors in all forms
  const isFieldModified = (path: string) => { // check's if the specific field in the form is modified
    return ( get(data, path, "") !== get(initialDataRef.current, path, ""));
  };

  const resetField = (path: string, resetFn: (val?: any) => void) => { // reset the specific field in form, either initial value or new value.
    const modified = isFieldModified(path);
    const newValue = get(data, path, "");
    resetFn(modified ? newValue : false);
  };

  const resetInputEditors = () => { // ultimate reset function for all input editors in theme & gating.
   resetField("librarySettings.passwordText.buttonText",PasswordButton.reset); // password protection
   resetField("librarySettings.passwordText.placeHolder",PasswordInputPlaceholder.reset);

   resetField("librarySettings.gatingText.buttonText",GatingButton.reset); // gating
   resetField("librarySettings.gatingText.placeHolder",GatingInputPlaceholder.reset);

   resetField("librarySettings.ndaText.buttonText",NDAButton.reset); // nda
   resetField("librarySettings.ndaText.placeHolder", NDAInputPlaceholder.reset);

   resetField("librarySettings.otpText.buttonText",OTPButton.reset); // otp
 };

  useImperativeHandle(ref, () => ({
     reset: resetInputEditors, // exposes the resetInputEditors method to the parent component (ViewerSettings) through referralInfoSaga. This allows ViewerSettings to trigger a reset of the input editors when needed.  
  }));

  // convert image to base64
  async function convertToBase64(src: File) {
    let blob: Blob;
    blob = src;

    return new Promise<string>((resolve) => {
      ImageTools.resize(blob, {
        width: 196, // maximum width
        height: 196 // maximum height
      }, (resizedBlob) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string); // base64 output
        reader.readAsDataURL(resizedBlob);
      });
    });
  }

  // configuration
  const viewerConfiguration = [
    {
      component: <FavIconViewer 
        icon={typeof get(data, "favIcon") === "string" ? get(data, "favIcon") : get(data, "favIcon.full") || FavIcon}
        tabName={get(data, "name", "collections")}
        tabURL={"www.paperflite.com/collections/"}
      />,
    }, 
    {
      component:   <FormViewer 
             logo={ get(data, "logo.full", CompanyLogo)}
             rendererContent = {
              <ThemeCustomizationViewer
                  buttonTextColor={get(data, "librarySettings.buttonTextColor") || "#FFF"}
                  buttonColor={get(data, "librarySettings.primaryColor") || "#e51058"} 
              />
              } 
    />
    },
    {
    component:  <FormViewer 
             logo={ get(data, "logo.full", CompanyLogo)}
             rendererContent = {
             <PasswordProtectionViewer 
                HeaderText={PasswordHeader.Renderer}
                Description={PasswordDescription.Renderer}
                ButtonText={PasswordButton.Render} 
                InputPlaceHolderText={PasswordInputPlaceholder.Render} 
                buttonColor={get(data, "librarySettings.primaryColor") || "#e51058"}  
                buttonTextColor={get(data, "librarySettings.buttonTextColor") || "#FFF"}
              />} 
    />
   },
   {
    component:  <FormViewer 
             logo={ get(data, "logo.full", CompanyLogo)}
             rendererContent = {
             <GatingCustomizationViewer
                HeaderText={GatingHeader.Renderer}
                Description={GatingDescription.Renderer}
                ButtonText={GatingButton.Render} 
                InputPlaceHolderText={GatingInputPlaceholder.Render}
                buttonColor={get(data, "librarySettings.primaryColor") || "#e51058"}  
                buttonTextColor={get(data, "librarySettings.buttonTextColor") || "#FFF"}
              />} 
    />
   },
   {
    component: <FormViewer 
             logo={ get(data, "logo.full", CompanyLogo)}
             rendererContent = {
             <NDACustomizationViewer
               HeaderText={NDAHeader.Renderer}
               Description={NDADescription.Renderer}
               ButtonText={NDAButton.Render} 
               InputPlaceHolderText={NDAInputPlaceholder.Render}
               buttonColor={get(data, "librarySettings.primaryColor") || "#e51058"}  
              buttonTextColor={get(data, "librarySettings.buttonTextColor") || "#FFF"}
              />
        } 
      />
   },
   {
    component: <FormViewer 
             logo={ get(data, "logo.full", CompanyLogo)}
             rendererContent = {
             <OtpCustomizationViewer
                HeaderText={OTPHeader.Renderer}
                Description={OTPDescription.Renderer}
                ButtonText={OTPButton.Render}
                buttonColor={get(data, "librarySettings.primaryColor") || "#e51058"}  
                buttonTextColor={get(data, "librarySettings.buttonTextColor") || "#FFF"}
             />
            } 
      />
   }
  ];
  
  const editorConfiguration = [
     {
      title: "Favicon",
      component:   <FavIconEditor
          icon={typeof get(data, "favIcon") === "string" ? get(data, "favIcon") : get(data, "favIcon.full") || FavIcon} 
          onChange={async (val) => {
            const base64 = await convertToBase64(val); // convert to base64
            handleChange?.("favIcon", base64);
          }} 
        />
     },
     {
      title: "Customize Theme",
      component:   <ThemeCustomizationEditor
        primaryColor={get(data, "librarySettings.primaryColor") || "#e51058"}
        buttonColor={get(data, "librarySettings.buttonTextColor") || "#FFF"}
        onColorChange={(title, color) => {
          handleChange?.(`librarySettings.${title}`, color);
        }}
      />
     },
     {
         title: "Password Protect Customization",
         component:  <PasswordProtectionEditor 
          HeaderText={PasswordHeader.TinyMce}
          Description={PasswordDescription.TinyMce}
          ButtonText={PasswordButton.Editor} 
          InputPlaceHolderText={PasswordInputPlaceholder.Editor} 
        />
       },
        {
         title: "Gating Customization",
         component: <GatingCustomizationEditor
          HeaderText={GatingHeader.TinyMce}
          Description={GatingDescription.TinyMce}
          ButtonText={GatingButton.Editor} 
          InputPlaceHolderText={GatingInputPlaceholder.Editor}
        /> 
        },
        {
         title: "NDA Customization",
         component: <NDACustomizationEditor
          HeaderText={NDAHeader.TinyMce}
          Description={NDADescription.TinyMce}
          ButtonText={NDAButton.Editor} 
          InputPlaceHolderText={NDAInputPlaceholder.Editor}
          /> 
        },
        {
          title: "OTP Customization",
          component: <OtpCustomizationEditor
          HeaderText={OTPHeader.TinyMce}
          Description={OTPDescription.TinyMce}
          ButtonText={OTPButton.Editor}
       />
        },
  ];
  
  return (
     <div className={styles.container}>
            <Actions.IconButton
                  icon={"paperflite-up-arrow"}
                  className={cx(styles.previewAction, { [styles.hide]: showEditor })}
                  onClick={() => {
                    toggleEditor();
                  }}
            />
                       
            <SyncScrollLayout 
                  viewerRenderer={viewerConfiguration}
                  editorRenderer={editorConfiguration}  
                  showEditor={showEditor}
                  editorGapBetweenSections={0}
                  enablePrevAndNextEditor={true}
            />
    </div>
    );
})

export const FavIconViewer = ({icon, tabName, tabURL}: {icon?: any, tabName: string, tabURL: string}) => {
  return(
    <div className={styles.favIconViewerContainer}>
        <div className={styles.tabBar}>
          <div className={styles.tabs}>  
            <div className={styles.circleIcons}>
              <span className={cx(styles.circle)}></span>
              <span className={cx(styles.circle)}></span>
              <span className={cx(styles.circle)}></span>
            </div>
            <div className={styles.tab}>
              <div>
                   <div className={styles.favIconContainer}>
                   {icon && <img src={icon} alt="Favicon" />}
              </div>
              <p> {tabName} </p>
              </div>
              <i className="fa-solid fa-xmark"></i>
            </div>
             <i className={cx(styles.closeTabIcon,"paperflite-plus")}></i>
          </div>
          <div className={styles.urlWrapper}>
            <div className={styles.iconsWrapper}>
                 <i className={cx("paperflite-left-arrow")}></i>
                  <i className={cx("paperflite-right-arrow", styles.iconDisable)}></i>
                    <i className={cx("paperflite-reload", styles.reload)}></i>
                      <i className={cx("paperflite-dashboard")}></i>
            </div>
            <div className={styles.urlBar}>
              <i className={cx("paperflite-lock")}></i>
              <p>{tabURL}</p>
              <i className={cx("fa-regular fa-star")}></i>
            </div>
            <i className="fa-solid fa-ellipsis-vertical"></i>
          </div>
          
        </div>
        <div className={styles.emptyArea}></div>
    </div>
  )
}

export const FormViewer = ({logo, rendererContent}: {logo:string, rendererContent: React.ReactNode}) => {
    return(
      <div className={styles.formViewerContainer}>
            <div className={styles.formContainer}>
                <img src={logo} alt=""  className={styles.logo}/>
                {rendererContent}
            </div>
           
            <div className={styles.imageContainer}>
                <img src={DefaultBanner} alt=""  className={styles.banner}/>
            </div>
      </div>
    )
}

